import React, { useEffect, useState } from "react";
import Stack from "@mui/material/Stack";
import {
  Button,
  Grid,
  Typography,
  ButtonGroup,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TableFooter,
  TableSortLabel,
  InputAdornment,
  Link,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";

import { usePagination } from "app/hooks/usePagination";
import { useDispatch, useSelector } from "react-redux";
import Loader from "@jumbo/components/Loader";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder/JumboListNoDataPlaceholder";
import {
  clearVariantSkus,
  getAppSalesReport,
  getVariantSkus,
} from "app/redux/slice/appSalesReportSlice";
import VariantSkuDrawer from "./VariantSkuDrawer";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import moment from "moment";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "60d",
    label: "Last 60D",
    getRange: () => [
      moment().subtract(59, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const AppSalesReport = () => {
  const dispatch = useDispatch();

  const reportData = useSelector((state) => state?.appSalesReport);

  const [dateRange, setDateRange] = useState(
    QUICK_RANGES.find((r) => r.key === "30d").getRange(),
  );
  const [tempDateRange, setTempDateRange] = useState(
    QUICK_RANGES.find((r) => r.key === "30d").getRange(),
  );

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("variantName");
  const [isInitialized, setIsInitialized] = useState(false);

  // `searchText` is what the box shows, `search` is what the API is called
  // with. Like tempDateRange/dateRange, typing only stages the term - it is
  // applied on Apply Filters.
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "app-sales-report" });

  // The row whose SKUs are open in the side drawer.
  const [skuRow, setSkuRow] = useState(null);

  const openSkus = (row) => {
    setSkuRow(row);
    dispatch(getVariantSkus(row.variantId));
  };

  const closeSkus = () => {
    setSkuRow(null);
    dispatch(clearVariantSkus());
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    handleChangePage(0, 0);
  };

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  const getData = () => {
    const value = {
      page: page,
      perPage: rowsPerPage,
    };

    if (search !== "") {
      value.search = search;
    }

    if (dateRange[0] !== null && dateRange[1] !== null) {
      value.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
      value.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    }

    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getAppSalesReport(value));
  };

  useEffect(() => {
    if (isInitialized) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    dispatch,
    order,
    orderBy,
    dateRange,
    search,
    isInitialized,
  ]);

  const applyFilters = () => {
    setDateRange(tempDateRange);
    setSearch(searchText.trim());
    handleChangePage(0, 0); // reset page to 1
  };

  const clearAll = () => {
    setTempDateRange([null, null]);
    setDateRange([null, null]);
    setSearchText("");
    setSearch("");
    handleChangePage(0, 0);
  };

  // Grand Total Calculation for current page
  const grandTotal = reportData?.list?.reduce(
    (acc, row) => {
      acc.myntraIn += row.platformBreakdown?.["5"]?.in || 0;
      acc.meeshoIn += row.platformBreakdown?.["3"]?.in || 0;
      acc.flipkartIn += row.platformBreakdown?.["2"]?.in || 0;
      acc.totalIn += row.totalIn || 0;

      acc.myntraOut += row.platformBreakdown?.["5"]?.out || 0;
      acc.meeshoOut += row.platformBreakdown?.["3"]?.out || 0;
      acc.flipkartOut += row.platformBreakdown?.["2"]?.out || 0;
      acc.totalOut += row.totalOut || 0;

      // Stock is a standing figure, not a flow, so this is what the rows on
      // screen are holding right now - the same reading as every other
      // column of this footer.
      acc.currentStock += row.currentStock || 0;

      return acc;
    },
    {
      myntraIn: 0,
      meeshoIn: 0,
      flipkartIn: 0,
      totalIn: 0,
      myntraOut: 0,
      meeshoOut: 0,
      flipkartOut: 0,
      totalOut: 0,
      currentStock: 0,
    },
  );

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        App Sales Report
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Search product"
                placeholder="Search by product name"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    applyFilters();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        display: searchText === "" ? "none" : "flex",
                        cursor: "pointer",
                      }}
                      onClick={() => setSearchText("")}
                    >
                      <ClearIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={tempDateRange}
                  onChange={setTempDateRange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={5}>
              <ButtonGroup size="small" fullWidth variant="outlined">
                {QUICK_RANGES.map((r) => (
                  <Button
                    key={r.key}
                    onClick={() => setTempDateRange(r.getRange())}
                  >
                    {r.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  fullWidth
                  size="small"
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearAll}
                  startIcon={<RefreshIcon />}
                  fullWidth
                  size="small"
                >
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {reportData?.loading ? (
            <Loader />
          ) : reportData?.list?.length === 0 ? (
            <JumboListNoDataPlaceholder />
          ) : (
            <Paper sx={{ width: "100%", overflow: "hidden" }}>
              <TableContainer>
                <Table stickyHeader aria-label="sticky table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        rowSpan={2}
                        sx={{
                          fontWeight: "bold",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === "variantName"}
                          direction={orderBy === "variantName" ? order : "asc"}
                          onClick={() => handleRequestSort("variantName")}
                        >
                          Product
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        colSpan={4}
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          borderBottom: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        In
                      </TableCell>
                      <TableCell
                        colSpan={4}
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          borderBottom: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        Out
                      </TableCell>
                      <TableCell rowSpan={2} sx={{ fontWeight: "bold" }}>
                        <TableSortLabel
                          active={orderBy === "currentStock"}
                          direction={orderBy === "currentStock" ? order : "asc"}
                          onClick={() => handleRequestSort("currentStock")}
                        >
                          Current Stock
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      {/* In */}
                      <TableCell sx={{ fontWeight: "bold" }}>
                        <TableSortLabel
                          active={orderBy === "myntraIn"}
                          direction={orderBy === "myntraIn" ? order : "asc"}
                          onClick={() => handleRequestSort("myntraIn")}
                        >
                          Myntra
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        <TableSortLabel
                          active={orderBy === "meeshoIn"}
                          direction={orderBy === "meeshoIn" ? order : "asc"}
                          onClick={() => handleRequestSort("meeshoIn")}
                        >
                          Meesho
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        <TableSortLabel
                          active={orderBy === "flipkartIn"}
                          direction={orderBy === "flipkartIn" ? order : "asc"}
                          onClick={() => handleRequestSort("flipkartIn")}
                        >
                          Flipkart
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === "totalIn"}
                          direction={orderBy === "totalIn" ? order : "asc"}
                          onClick={() => handleRequestSort("totalIn")}
                        >
                          Total
                        </TableSortLabel>
                      </TableCell>
                      {/* Out */}
                      <TableCell sx={{ fontWeight: "bold" }}>
                        <TableSortLabel
                          active={orderBy === "myntraOut"}
                          direction={orderBy === "myntraOut" ? order : "asc"}
                          onClick={() => handleRequestSort("myntraOut")}
                        >
                          Myntra
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        <TableSortLabel
                          active={orderBy === "meeshoOut"}
                          direction={orderBy === "meeshoOut" ? order : "asc"}
                          onClick={() => handleRequestSort("meeshoOut")}
                        >
                          Meesho
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        <TableSortLabel
                          active={orderBy === "flipkartOut"}
                          direction={orderBy === "flipkartOut" ? order : "asc"}
                          onClick={() => handleRequestSort("flipkartOut")}
                        >
                          Flipkart
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === "totalOut"}
                          direction={orderBy === "totalOut" ? order : "asc"}
                          onClick={() => handleRequestSort("totalOut")}
                        >
                          Total
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData?.list?.map((row, index) => {
                      const myntraIn = row.platformBreakdown?.["5"]?.in || 0;
                      const meeshoIn = row.platformBreakdown?.["3"]?.in || 0;
                      const flipkartIn = row.platformBreakdown?.["2"]?.in || 0;

                      const myntraOut = row.platformBreakdown?.["5"]?.out || 0;
                      const meeshoOut = row.platformBreakdown?.["3"]?.out || 0;
                      const flipkartOut =
                        row.platformBreakdown?.["2"]?.out || 0;

                      return (
                        <TableRow hover key={index}>
                          <TableCell
                            sx={{
                              borderRight: "1px solid rgba(224, 224, 224, 1)",
                            }}
                          >
                            {/* Opens this variant's SKUs, platform by
                                platform. A button, so it works from the
                                keyboard too. */}
                            {row.variantId ? (
                              <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={() => openSkus(row)}
                                sx={{ textAlign: "left", fontWeight: 500 }}
                              >
                                {row.variantName}
                              </Link>
                            ) : (
                              row.variantName
                            )}
                          </TableCell>

                          <TableCell>{myntraIn}</TableCell>
                          <TableCell>{meeshoIn}</TableCell>
                          <TableCell>{flipkartIn}</TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              borderRight: "1px solid rgba(224, 224, 224, 1)",
                            }}
                          >
                            {row.totalIn}
                          </TableCell>

                          <TableCell>{myntraOut}</TableCell>
                          <TableCell>{meeshoOut}</TableCell>
                          <TableCell>{flipkartOut}</TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              borderRight: "1px solid rgba(224, 224, 224, 1)",
                            }}
                          >
                            {row.totalOut}
                          </TableCell>

                          <TableCell
                            sx={{
                              color:
                                row.currentStock < row.totalOut
                                  ? "error.main"
                                  : "inherit",
                            }}
                          >
                            {row.currentStock}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  <TableFooter sx={{ backgroundColor: "rgba(0, 0, 0, 0.04)" }}>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          fontSize: "1rem",
                        }}
                      >
                        GRAND TOTAL
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {grandTotal?.myntraIn}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {grandTotal?.meeshoIn}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {grandTotal?.flipkartIn}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          fontSize: "1rem",
                        }}
                      >
                        {grandTotal?.totalIn}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {grandTotal?.myntraOut}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {grandTotal?.meeshoOut}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {grandTotal?.flipkartOut}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          fontSize: "1rem",
                        }}
                      >
                        {grandTotal?.totalOut}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          fontSize: "1rem",
                          color:
                            grandTotal?.currentStock < 0
                              ? "error.main"
                              : "inherit",
                        }}
                      >
                        {grandTotal?.currentStock}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[10, 25, 100, 500]}
                component="div"
                count={reportData?.total || 0}
                rowsPerPage={rowsPerPage}
                page={page === 0 ? 0 : page - 1}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          )}
        </CardContent>
      </Card>

      <VariantSkuDrawer
        open={Boolean(skuRow)}
        row={skuRow}
        state={reportData?.variantSkus}
        onClose={closeSkus}
      />
    </Box>
  );
};

export default AppSalesReport;
